import { AppRoute } from "./Routes";
import { Button, ConfigProvider, theme } from "antd";
import { usePreferencesContext } from "./context/preferences/context";
import { I18nextProvider, useTranslation } from "react-i18next";
import { useNotifier } from "./providers/NotificationProvider";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { getLatestVersion } from "./common/util";

export function App() {
  const preferencesContext = usePreferencesContext();
  const { i18n } = useTranslation();
  const notifier = useNotifier();
  const [currentVersion, setCurrentVersion] = useState<string>("");

  const { defaultAlgorithm, darkAlgorithm } = theme;

  useEffect(() => {
    const bgColor = preferencesContext.state.isDarkMode
      ? "#141414" // Ant Design dark background
      : "#ffffff"; // Ant Design light background

    document.body.style.backgroundColor = bgColor;
    document.body.style.minHeight = "100vh";
    document.body.style.transition = "background-color 0.3s ease";

    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [preferencesContext.state.isDarkMode]);

  // Polling version check every 60 seconds
  useEffect(() => {
    if (!process.env.NODE_ENV.includes("prod")) return;

    const checkVersion = async () => {
      try {
        const version = await getLatestVersion();
        if (
          version &&
          version !== process.env.REACT_APP_BUILD_VERSION &&
          version !== currentVersion
        ) {
          setCurrentVersion(version);
          notifier.warning({
            key: "new-version",
            message: t("message.new_version_title"),
            description: t("message.new_version"),
            closable: true,
            duration: 0,
            actions: (
              <Button type="primary" onClick={() => window.location.reload()}>
                {t("button.update")}
              </Button>
            ),
            placement: "topRight",
          });
        }
      } catch (err) {
        console.error("Version check failed", err);
      }
    };

    // Initial check
    if (document.visibilityState === "visible") {
      checkVersion();
    }

    // Poll every 15 seconds but only if tab is visible
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVersion, notifier, t]);

  return (
    <I18nextProvider i18n={i18n}>
      <ConfigProvider
        locale={preferencesContext.state.antdLocale}
        direction={preferencesContext.state.direction}
        theme={{
          algorithm: preferencesContext.state.isDarkMode
            ? darkAlgorithm
            : defaultAlgorithm,
          token: {
            colorPrimary: "#fe6a00",
            colorLink: "#fe6a00",
            // antd components render from `token.fontFamily`, not from the `body`
            // rule in override.css — without this they stay on the system stack
            // while the text around them is Inter.
            fontFamily:
              '"Inter", "Thmanyah Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          },
        }}
      >
        <AppRoute />
      </ConfigProvider>
    </I18nextProvider>
  );
}
